// ChangePasswordGate — обязательная смена временного пароля при первом входе.
// Рендерится Layout'ом ВМЕСТО контента, пока user.mustChangePassword=true:
// backend (AuthGuard) в этом состоянии всё равно отдаёт 403 на всё, кроме
// /auth/*, поэтому обойти экран нельзя. После успешной смены — refresh().
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, errorText } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';

const MIN_PASSWORD_LENGTH = 12;

export function ChangePasswordGate(): JSX.Element {
  const { refresh, signOut } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm.length > 0 && next !== confirm;
  const canSubmit =
    !pending && current.length > 0 && next.length >= MIN_PASSWORD_LENGTH && next === confirm;

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;
    setPending(true);
    setError(null);
    try {
      await api.post('/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      await refresh();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4">
      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="w-full max-w-md space-y-4 rounded-card border border-divider-gray bg-canvas-white p-6"
      >
        <div>
          <h1 className="text-heading-sm font-medium tracking-tight">Смените пароль</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Вы вошли с временным паролем. Задайте свой пароль (минимум {MIN_PASSWORD_LENGTH}{' '}
            символов), чтобы продолжить работу.
          </p>
        </div>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium">Временный пароль</span>
          <Input
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
          />
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium">Новый пароль</span>
          <Input
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            minLength={MIN_PASSWORD_LENGTH}
          />
          {tooShort ? (
            <span className="block text-xs text-destructive">
              Минимум {MIN_PASSWORD_LENGTH} символов
            </span>
          ) : null}
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium">Новый пароль ещё раз</span>
          <Input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
          {mismatch ? (
            <span className="block text-xs text-destructive">Пароли не совпадают</span>
          ) : null}
        </label>

        {error ? (
          <p className="rounded-base bg-destructive/10 px-3 py-2 text-sm text-destructive">
            Не удалось сменить пароль: {error}
          </p>
        ) : null}

        <div className="flex items-center justify-between gap-3">
          <Button type="submit" disabled={!canSubmit}>
            {pending ? 'Сохраняем…' : 'Сменить пароль'}
          </Button>
          <button
            type="button"
            onClick={() => void signOut()}
            className="text-sm text-muted-foreground hover:text-ink-black"
          >
            Выйти
          </button>
        </div>
      </form>
    </div>
  );
}
